/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";
import { Alert, Box, useTheme, Button, Modal, Fade, TextField, Backdrop, CircularProgress, Stack } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DetailedUser, Effect, ModalBase, Nullable, StorageLocation, TechwiseOffice, User } from "../../utils/Interfaces";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { delayedAction } from "../../utils/Functions";
import { selectExistingUsers } from "../admin/user-management/userManagementSlice";
import { refreshUsers } from "../admin/user-management/userManagementFunctions";
import { modifyStorageLocation } from "./techwiseOfficeFunctions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

export interface AddLocationModalProps extends ModalBase {
    techwiseOffice: TechwiseOffice;
    storageLocation: Nullable<StorageLocation>;
    onStorageLocationModified: Effect;
}

const ModifyLocationModal = (props: AddLocationModalProps) => {
    const { open, handleClose, onStorageLocationModified, storageLocation } = props;
    const theme = useTheme();

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [name, setName] = useState<string>("");
    const [locationType, setLocationType] = useState<string>("Permanent");
    const [responsibleUser, setResponsibleUser] = useState<Nullable<DetailedUser>>(null);

    const [loading, setLoading] = useState<boolean>(false);

    const [usersLoaded, setUsersLoaded] = useState<boolean>(false);
    const existingUsers: DetailedUser[] = useAppSelector(selectExistingUsers);

    useEffect(() => {
        setName(storageLocation?.name ?? "");
        setLocationType(storageLocation?.location_type ?? "Permanent");
        setResponsibleUser((storageLocation?.responsible_user as DetailedUser) ?? null);
    }, [storageLocation]);

    useEffect(() => {
        if (!usersLoaded) {
            refreshUsers(dispatch, () => {});
            setUsersLoaded(true);
        }
    }, [open]);

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            setName(storageLocation?.name ?? "");
            setLocationType(storageLocation?.location_type ?? "Permanent");
            setResponsibleUser(null);
            setLoading(false);
        });
    }

    function getFormErrors(): string {
        if (name.trim() === "") {
            return "Name cannot be blank.";
        }

        if (locationType.trim() === "") {
            return "Street Address cannot be blank.";
        }

        if (responsibleUser === null) {
            return "There must be a selected user responsible for this storage location.";
        }

        return "";
    }

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={onClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                        }}
                    >
                        <Alert severity={getFormErrors() === "" ? "success" : "error"} sx={{ mb: 2 }}>
                            {getFormErrors() === "" ? "All changes are valid." : getFormErrors()}
                        </Alert>

                        <TextField
                            sx={{ width: 600 }}
                            margin="normal"
                            required
                            fullWidth
                            id="name"
                            label="Name"
                            name="name"
                            value={name}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                        />

                        <Autocomplete
                            disablePortal
                            onChange={(_event, value) => {
                                setLocationType(value ?? "Permanent");
                            }}
                            sx={{ mt: 2, width: 600 }}
                            id="location_type"
                            getOptionKey={(option: string) => option}
                            options={["Permanent", "Technician Van"]}
                            value={locationType}
                            getOptionLabel={(option: string) => option}
                            renderInput={(params) => <TextField required {...params} label="Location Type" />}
                        />

                        <Autocomplete
                            disablePortal
                            onChange={(_event, value) => {
                                setResponsibleUser(value ? value : null);
                            }}
                            sx={{ mt: 3 }}
                            id="responsible_user"
                            getOptionKey={(option: User) => option.id}
                            options={existingUsers}
                            value={responsibleUser}
                            getOptionLabel={(option: User) => `${option.first_name} ${option.last_name}`}
                            renderInput={(params) => <TextField required {...params} label="Responsible User" />}
                        />

                        {loading && (
                            <Stack direction="row" justifyContent="center" sx={{ mt: 4, mb: 3 }}>
                                <CircularProgress size={20} />
                            </Stack>
                        )}

                        {!loading && (
                            <Button
                                onClick={() => {
                                    setLoading(true);
                                    modifyStorageLocation(
                                        storageLocation?.id ?? 0,
                                        {
                                            name: name,
                                            location_type: locationType,
                                            responsible_user: responsibleUser?.id ?? 0,
                                        },
                                        dispatch,
                                        () => {
                                            onStorageLocationModified();
                                            onClose();
                                        }
                                    );
                                }}
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                color="primary"
                                disabled={getFormErrors() !== ""}
                            >
                                Submit
                            </Button>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default ModifyLocationModal;
